import { Component, ElementRef, EventEmitter, inject, input, OnDestroy, OnInit, viewChild, ViewEncapsulation } from '@angular/core';
import { EnpsComponent } from '@common/components/base/enps-component';
import { ModalService } from '@common/components/modal/modal.service';
import { LanguageSettings } from '@common/services/language-settings';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { AppService } from '@suite/app.service';
import { NgxUploaderModule, UploaderOptions, UploadFile, UploadInput, UploadOutput, UploadStatus } from 'ngx-uploader';
import { Subject } from 'rxjs';

import { AvatarBaseDirective } from './avatar-base';

@Component({
    selector: 'st-avatar[upload]',
    imports: [NgxUploaderModule],
    templateUrl: './avatar-upload.html',
    styleUrl: './avatar-upload.scss',
    encapsulation: ViewEncapsulation.None,
    host: { class: 'st-avatar-upload' },
})
export class AvatarComponent extends EnpsComponent implements OnInit, OnDestroy {
    appService = inject(AppService);
    languageSettings = inject(LanguageSettings);
    nomWebServicesJson = inject(NomWebServicesJson);
    modalService = inject(ModalService);

    readonly userId = input<string>();
    options: UploaderOptions;
    dragOver: boolean;
    private _imageUrl = '';

    get imageUrl(): string {
        if (this._imageUrl === '') {
            this._imageUrl = AvatarBaseDirective.buildAvatarUrl(this.appService.nomWebServicesJson, this.userId());
        }
        return this._imageUrl;
    }

    public set imageUrl(value: string) {
        this._imageUrl = value;
    }

    files: UploadFile[];
    uploadInput: EventEmitter<UploadInput>;
    readonly editAvatar = input<Subject<Event>>();
    readonly input = viewChild<ElementRef<HTMLInputElement>>('file');

    constructor() {
        super();
        this.files = [];
        this.uploadInput = new EventEmitter<UploadInput>();
    }

    ngOnInit() {
        this.editAvatar().subscribe(() => {
            this.openFile();
        });
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.editAvatar().complete();
    }

    openFile(): void {
        this.input().nativeElement.click();
    }

    onUploadOutput(output: UploadOutput): void {
        if (output.type === 'allAddedToQueue') {
            const event: UploadInput = {
                type: 'uploadAll',
                url: this.appService.nomWebServicesJson.getUploadEndPoint(),
                method: 'POST',
                data: {
                    userId: this.userId(),
                    AvatarImage: this.files[0].nativeFile,
                },
                headers: {
                    'X-ENPS-TOKEN': this.nomWebServicesJson.nomTokenId,
                },
            };
            this.uploadInput.emit(event);
        } else if (output.type === 'done') {
            if (output.file.responseStatus > 400) {
                this.modalService.openAlert({ title: 'Warning', content: output.file.response });
            }
            if (output.file.responseStatus === 200) {
                // Force every avatar render of this user to refetch the new image
                // (changes the URL via cache-bust token) and clear the local
                // lazy-cached URL on this component.
                AvatarBaseDirective.bumpAvatarVersion(this.userId());
                this._imageUrl = '';
                // Clear any prior load-error flag so the new image is allowed
                // to display. Must use signal.update(); `delete signal[key]`
                // operates on the function object, not the underlying record.
                AvatarBaseDirective.avatarErrorsByUserId.update(prev => {
                    const { [this.userId()]: _removed, ...rest } = prev;
                    return rest;
                });
            }
        } else if (output.type === 'addedToQueue' && typeof output.file !== 'undefined') {
            this.files[0] = output.file;
        } else if (output.type === 'cancelled' || output.type === 'removed') {
            this.files = this.files.filter((file: UploadFile) => file !== output.file);
        } else if (output.type === 'dragOver') {
            this.dragOver = true;
        } else if (output.type === 'dragOut') {
            this.dragOver = false;
        } else if (output.type === 'drop') {
            this.dragOver = false;
        }
        this.files = this.files.filter(file => file.progress.status !== UploadStatus.Done);
    }
}
